import React, { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import Pagination from 'react-js-pagination'
import useFullPageLoader from '../../hooks/useFullPageLoader'
import NotFound from '../../components/not-found/NotFound'
import IconButton from '../../components/Button/IconButton'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import { Avatar } from '@mui/material'
import { toast } from 'sonner'
import { fetchPlaystoreScreenshotsApi, grantPlaystoreScreenshotApi } from '../../services/playstoreScreenshot'
import { toastMessage } from '../../utils/toastMessage'
import scrollToUp from '../../utils/scrollToUp'
import { dateFormat, timeFormat } from '../../utils/dateFormat'
import profileImage from '../../Assests/image/profile_not_found.png'
import useButtonLoader from '../../hooks/UseButtonLoader'
import ModalBox from '../../components/modal/ModalBox'
import findSerialNumber from '../../utils/findSerialNumber'
import common from '../../routes/common'
import { user as userRoutes } from '../../routes/route'
import { IoSearch } from 'react-icons/io5'

const { NOT_FOUND } = common
const { USER_DETAIL } = userRoutes
const AWS_S3_BASE_URL = 'https://vedic-meet-bucket.s3.ap-south-1.amazonaws.com/'

const PlaystoreScreenshotManagement = () => {
  const [searchParam, setSearchParam] = useSearchParams()
  let activePage = +searchParam.get('page') || 1
  let pageLimit = +searchParam.get('limit') || 10
  const [screenshotList, setScreenshotList] = useState([])
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [loader, showLoader, hideLoader] = useFullPageLoader()
  const [buttonRef, buttonLoader] = useButtonLoader('Confirm')
  const [grantModal, setGrantModal] = useState(false)
  const [actionId, setActionId] = useState(null)
  const [actionType, setActionType] = useState(null)
  const [previewModal, setPreviewModal] = useState(false)
  const [previewImage, setPreviewImage] = useState('')
  const [previewTitle, setPreviewTitle] = useState('')

  // Get image URL with S3 prefix
  const getImageUrl = (url) => {
    if (!url) return profileImage
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    // Add S3 base URL prefix
    return `${AWS_S3_BASE_URL}${url.startsWith('/') ? url.substring(1) : url}`
  }

  // Fetch playstore screenshots
  const fetchScreenshots = async () => {
    try {
      setLoading(true)
      showLoader()
      const { data } = await fetchPlaystoreScreenshotsApi()
      if (data.success) {
        let filteredData = data.data || []
        
        // Apply search filter
        if (search) {
          const searchLower = search.toLowerCase()
          filteredData = filteredData.filter((item) => {
            const userName = item.userId?.name?.toLowerCase() || ''
            const userEmail = item.userId?.email?.toLowerCase() || ''
            const userPhone = item.userId?.phone?.toLowerCase() || ''
            const vedicUserId = String(item.userId?.userId ?? '').toLowerCase()
            return (
              userName.includes(searchLower) ||
              userEmail.includes(searchLower) ||
              userPhone.includes(searchLower) ||
              vedicUserId.includes(searchLower)
            )
          })
        }
        
        setScreenshotList(filteredData)
        setTotal(filteredData.length)
        scrollToUp()
      } else {
        setScreenshotList([])
        setTotal(0)
        toastMessage(data.message || 'Failed to fetch screenshots', 'error')
      }
    } catch (error) {
      console.error('Error fetching screenshots:', error)
      toastMessage('Failed to fetch screenshots', 'error')
      setScreenshotList([])
      setTotal(0)
    } finally {
      setLoading(false)
      hideLoader()
    }
  }

  // Handle pagination
  const handlePageChange = (event) => {
    searchParam.set('page', event)
    setSearchParam(searchParam)
  }

  const handlePageLimitChange = (event) => {
    const newLimit = parseInt(event.target.value)
    searchParam.set('limit', newLimit)
    searchParam.set('page', 1)
    setSearchParam(searchParam)
  }

  // Handle refresh
  const refreshHandler = () => {
    setSearch('')
    searchParam.delete('page')
    searchParam.delete('limit')
    setSearchParam(searchParam)
    fetchScreenshots()
  }

  // Handle grant/reject action
  const handleGrantReject = async () => {
    try {
      buttonLoader(true)
      const { data } = await grantPlaystoreScreenshotApi({
        id: actionId,
        isGranted: actionType === 'grant',
      })

      if (data.success) {
        toast.success(
          actionType === 'grant'
            ? 'Screenshot granted successfully. 20 coins added to user wallet.'
            : 'Screenshot rejected successfully'
        )
        setGrantModal(false)
        setActionId(null)
        setActionType(null)
        fetchScreenshots()
      } else {
        toast.error(data.message || 'Failed to update screenshot status')
      }
    } catch (error) {
      console.error('Error updating screenshot:', error)
      toast.error('Failed to update screenshot status')
    } finally {
      buttonLoader(false)
    }
  }

  // Open grant/reject modal
  const openGrantModal = (id, type) => {
    setActionId(id)
    setActionType(type)
    setGrantModal(true)
  }

  // Preview screenshot
  const previewScreenshot = (screenshot) => {
    const userInfo = getUserInfo(screenshot.userId)
    setPreviewImage(getImageUrl(screenshot.screenshotUrl))
    setPreviewTitle(`Playstore Screenshot - ${userInfo.name}`)
    setPreviewModal(true)
  }

  // Get user display info
  const getUserInfo = (user) => {
    if (!user) {
      return {
        name: 'Unknown User',
        email: null,
        phone: null,
        avatar: profileImage,
        vedicUserId: null,
        mongoId: null,
      }
    }
    return {
      name: user.name || 'Unknown User',
      email: user.email || null,
      phone: user.phone || null,
      avatar: user.image || profileImage,
      vedicUserId: user.userId || null,
      mongoId: user._id || null,
    }
  }

  // Get paginated data
  const paginatedData = screenshotList.slice(
    (activePage - 1) * pageLimit,
    activePage * pageLimit
  )

  useEffect(() => {
    if (Number(activePage) === 1) {
      searchParam.delete('page')
    }
    setSearchParam(searchParam)
    fetchScreenshots()
  }, [searchParam, search])

  return (
    <>
      {loader}
      <div className="WrapperArea">
        <div className="WrapperBox">
          <div className="TitleBox">
            <h4 className="Title">Playstore Screenshot Management</h4>
            <div className="block_user_button">
              <IconButton
                aria-label="refresh"
                className="px-2 py-0"
                onClick={refreshHandler}
              >
                <RotateLeftIcon className="eye_icon" />
              </IconButton>
            </div>
          </div>

          <div className="FilterBox FilterArea">
            <div className="form-group search_lg_size userSearch">
              <input
                type="search"
                className={`form-control ${search && 'active'}`}
                placeholder="Search by name, email, phone, or user ID"
                value={search || ''}
                style={{ '--color': '#FF8989' }}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span className="user_search_icon">
                <IoSearch />
              </span>
            </div>
          </div>

          <div className="content-table">
            <table>
              <thead>
                <tr>
                  <th>S. No.</th>
                  {/* <th>Profile</th> */}
                  <th>User ID</th>
                  <th>User Name</th>
                  {/* <th>Email</th>
                  <th>Phone</th> */}
                  {/* <th>Screenshot</th> */}
                  <th>Uploaded Date</th>
                  {/* <th>Uploaded Time</th> */}
                  <th>Status</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((screenshot, index) => {
                    const userInfo = getUserInfo(screenshot.userId)
                    return (
                      <React.Fragment key={screenshot._id}>
                        <tr>
                          <td>{findSerialNumber(index, activePage, pageLimit)}</td>
                          {/* <td className="user_profile_image">
                            <Avatar
                              src={getImageUrl(userInfo.avatar)}
                              alt={userInfo.name}
                              sx={{
                                width: 50,
                                height: 50,
                                borderRadius: '50%',
                                cursor: 'pointer',
                              }}
                              onError={(e) => {
                                e.target.src = profileImage
                              }}
                            />
                          </td> */}
                          <td>
                            {userInfo.mongoId && userInfo.vedicUserId ? (
                              <Link
                                to={`${USER_DETAIL}/${userInfo.mongoId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#1976d2', fontWeight: 500, textDecoration: 'none' }}
                              >
                                {userInfo.vedicUserId}
                              </Link>
                            ) : userInfo.mongoId ? (
                              <Link
                                to={`${USER_DETAIL}/${userInfo.mongoId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#1976d2', fontWeight: 500, textDecoration: 'none' }}
                              >
                                View user
                              </Link>
                            ) : (
                              '---'
                            )}
                          </td>
                          <td>{userInfo.name}</td>
                          {/* <td>{userInfo.email || '---'}</td>
                          <td>
                            {userInfo.phone ? (
                              <a
                                href={`tel:${userInfo.phone}`}
                                style={{ color: '#FF8989', textDecoration: 'none' }}
                              >
                                {userInfo.phone}
                              </a>
                            ) : (
                              '---'
                            )}
                          </td> */}
                          {/* <td>
                            {screenshot.screenshotUrl ? (
                              <button
                                className="btn btn-sm"
                                style={{
                                  backgroundColor: '#1890ff',
                                  color: 'white',
                                  border: 'none',
                                  padding: '5px 15px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                }}
                                onClick={() => previewScreenshot(screenshot)}
                              >
                                View
                              </button>
                            ) : (
                              '---'
                            )}
                          </td> */}
                          <td>{screenshot.createdAt ? dateFormat(screenshot.createdAt) : '---'}</td>
                          {/* <td>{screenshot.createdAt ? timeFormat(screenshot.createdAt) : '---'}</td> */}
                          <td>
                            {screenshot.isGranted ? (
                              <span style={{ color: '#52c41a', fontWeight: 'bold' }}>Approved</span>
                            ) : screenshot.status === false ? (
                              <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>Rejected</span>
                            ) : (
                              <span style={{ color: '#faad14', fontWeight: 'bold' }}>Pending</span>
                            )}
                          </td>
                          <td>
                            <div className="action_button">
                              <IconButton
                                aria-label="view"
                                className="px-2 py-0"
                                onClick={() => previewScreenshot(screenshot)}
                              >
                                <VisibilityIcon className="eye_icon" />
                              </IconButton>
                              {!screenshot.isGranted && screenshot.status !== false && (
                                <>
                                  <IconButton
                                    aria-label="grant"
                                    className="px-2 py-0"
                                    onClick={() => openGrantModal(screenshot._id, 'grant')}
                                    style={{ color: '#52c41a' }}
                                  >
                                    <CheckCircleIcon />
                                  </IconButton>
                                  <IconButton
                                    aria-label="reject"
                                    className="px-2 py-0"
                                    onClick={() => openGrantModal(screenshot._id, 'reject')}
                                    style={{ color: '#ff4d4f' }}
                                  >
                                    <CloseIcon />
                                  </IconButton>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    )
                  })
                ) : (
                  <NotFound
                    cols={7}
                    msg="No screenshots found!"
                    icon={NOT_FOUND}
                    loader={loader}
                  />
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-wrapper mt-4 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <span style={{ fontSize: '14px', color: '#666' }}>Show:</span>
              <select
                value={pageLimit}
                onChange={(e) => handlePageLimitChange(e)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '80px',
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span style={{ fontSize: '14px', color: '#666' }}>per page</span>
            </div>

            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: '14px', color: '#666' }}>
                Showing {((activePage - 1) * pageLimit) + 1} to {Math.min(activePage * pageLimit, total)} of {total} items
              </span>
            </div>

            {total > pageLimit && (
              <div className="pagination-wrapper">
                <div className="Pagination">
                  <Pagination
                    activePage={activePage}
                    previousLabel={'previous'}
                    nextLabel={'next'}
                    itemsCountPerPage={pageLimit}
                    totalItemsCount={total}
                    pageRangeDisplayed={5}
                    onChange={handlePageChange}
                    hideDisabled={true}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewModal && (
        <ModalBox
          status={previewModal}
          onClose={() => setPreviewModal(false)}
          title={previewTitle}
          size="md"
          closeIcon={true}
          customizedFooter={
            <button
              onClick={() => setPreviewModal(false)}
              className="custom_button mx-2"
            >
              Close
            </button>
          }
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '20px',
              minHeight: '400px',
            }}
          >
            <img
              src={previewImage}
              alt={previewTitle}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: 8,
              }}
              onError={(e) => {
                e.target.style.display = 'none'
                toast.error('Failed to load image')
              }}
            />
          </div>
        </ModalBox>
      )}

      {/* Grant/Reject Confirmation Modal */}
      {grantModal && (
        <ModalBox
          status={grantModal}
          onClose={() => {
            setGrantModal(false)
            setActionId(null)
            setActionType(null)
          }}
          title={actionType === 'grant' ? 'Grant Screenshot' : 'Reject Screenshot'}
          description={
            actionType === 'grant' ? (
              <>
                Are you sure you want to grant this screenshot?{' '}
                <strong style={{ color: '#52c41a' }}>20 coins</strong> will be added to the user's wallet.
              </>
            ) : (
              'Are you sure you want to reject this screenshot? This action cannot be undone.'
            )
          }
          action={handleGrantReject}
          ref={buttonRef}
        />
      )}
    </>
  )
}

export default PlaystoreScreenshotManagement
